import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Box } from "./canvas";
import { createShapeId, type TLShapeId } from "@tldraw/tlschema";
import { useValue } from "@tldraw/state-react";
import type { WorkspaceModel } from "./useWorkspace";
import {
  sketchSelection,
  SKETCH_PREVIEW_SIZE,
  SKETCH_PROMPT,
} from "../shared/sketch-generation";
import { creativePlacement } from "./creative-tools";
import { useImageGeneration } from "./useImageGeneration";
import { ImageGenerationActions } from "./ImageGenerationActions";
import { ImageGenerationResult } from "./ImageGenerationResult";
import type { ImageJob } from "../shared/creative-generation";
import "./creative-tools.css";

type Snapshot = {
  ids: TLShapeId[];
  key: string;
  hash: string;
  blob: Blob;
  url: string;
};

/** Explicit selection only: never flatten a whole sheet, references or comments. */
export function SketchTools({
  model,
  ids,
  close,
}: {
  model: WorkspaceModel;
  ids: TLShapeId[];
  close: () => void;
}) {
  const { editor } = model;
  const [prompt, setPrompt] = useState(SKETCH_PROMPT);
  const [size, setSize] = useState<ImageJob["size"]>("2K");
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState("");
  const [height, setHeight] = useState(450);
  const content = useRef<HTMLDivElement>(null);
  const ownedUrl = useRef("");
  const captureId = useRef(0);
  const generation = useImageGeneration(model, true, "sketch");
  const state = useValue(
    "sketch selection",
    () => {
      if (!model.canEdit || editor.getIsReadonly()) return null;
      const selected = editor.getSelectedShapeIds();
      if (
        selected.length !== ids.length ||
        ids.some((id) => !selected.includes(id))
      )
        return null;
      try {
        const selection = sketchSelection(ids, (id) =>
          editor.store.get(id as TLShapeId),
        );
        const boxes = ids
          .map((id) => editor.getShapePageBounds(id))
          .filter((b): b is Box => !!b);
        if (boxes.length !== ids.length) return null;
        const bounds = Box.Common(boxes);
        const view = editor.getViewportScreenBounds();
        const bottom = editor.pageToScreen({
          x: bounds.center.x,
          y: bounds.maxY,
        });
        const right = editor.pageToScreen({
          x: bounds.maxX,
          y: bounds.center.y,
        });
        return {
          ...selection,
          bounds,
          view,
          anchor: {
            x: bottom.x - view.x,
            bottom: bottom.y - view.y,
            right: right.x - view.x,
          },
        };
      } catch {
        return null;
      }
    },
    [editor, ids, model.canEdit],
  );
  const selectionId = [...ids].sort().join(",");
  const job = generation.status.jobs.find(
    (j) =>
      [...(j.sourceIds || [])].sort().join(",") === selectionId &&
      (!j.sourceKey || j.sourceKey === snapshot?.hash),
  );
  const placed = useValue(
    "sketch result placed",
    () => !!job && !!editor.getShape(createShapeId(`ai-${job.id}`)),
    [editor, job?.id],
  );
  const stale = !!snapshot && snapshot.key !== state?.key;
  const generating =
    generation.sending ||
    generation.status.jobs.some((j) => j.state === "running");

  const capture = async () => {
    if (!state) return;
    const attempt = ++captureId.current;
    setPreparing(true);
    setError("");
    try {
      const bounds = state.bounds.clone().expandBy(16);
      const result = await editor.toImage(ids, {
        format: "png",
        background: true,
        bounds,
        padding: 0,
        scale: Math.min(2, SKETCH_PREVIEW_SIZE / Math.max(bounds.w, bounds.h)),
        pixelRatio: 1,
      });
      if (attempt !== captureId.current) return;
      const current = sketchSelection(ids, (id) =>
        editor.store.get(id as TLShapeId),
      );
      if (current.key !== state.key)
        throw new Error(
          "The sketch changed while preparing. Refresh the preview.",
        );
      if (!result.blob.size || result.blob.size > 7_000_000)
        throw new Error("Select a smaller sketch to create an image.");
      const digest = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(current.key),
      );
      if (attempt !== captureId.current) return;
      const hash = Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join("");
      const url = URL.createObjectURL(result.blob);
      if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current);
      ownedUrl.current = url;
      setSnapshot({
        ids: current.ids,
        key: current.key,
        hash,
        blob: result.blob,
        url,
      });
    } catch (e) {
      if (attempt === captureId.current) setError((e as Error).message);
    } finally {
      if (attempt === captureId.current) setPreparing(false);
    }
  };
  useEffect(() => {
    void capture();
    return () => {
      captureId.current++;
      if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current);
    };
    // Capture once on opening; edits require an explicit preview refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionId]);
  useEffect(() => {
    if (!state || model.modal || model.cropping) close();
  }, [state, model.modal, model.cropping, close]);
  useLayoutEffect(() => {
    const node = content.current;
    if (!node) return;
    const measure = () => setHeight(node.scrollHeight + 30);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  if (!state) return null;
  const { docked, ...position } = creativePlacement(
    state.view,
    state.anchor,
    height,
  );
  return (
    <section
      className="creative-composer floating-surface"
      style={position}
      data-docked={docked}
      aria-label="Create from sketch"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          close();
          editor.focus();
        }
      }}
    >
      <div ref={content}>
        <header className="creative-heading">
          <div>
            <strong>Create from sketch</strong>
            <small>Nano Banana Pro</small>
          </div>
          <button aria-label="Close sketch tools" onClick={close}>
            <X size={17} />
          </button>
        </header>
        <div className="sketch-preview">
          {snapshot &&
            !(
              job?.state === "ready" &&
              job.output &&
              job.sourceKey === snapshot.hash
            ) && <img src={snapshot.url} alt="Selected sketch preview" />}
          {preparing && <small role="status">Preparing your sketch…</small>}
          <small>
            {ids.length} selected {ids.length === 1 ? "object" : "objects"} ·
            original kept
          </small>
        </div>
        <label className="sketch-output-size">
          Output size
          <select
            aria-label="Sketch output size"
            value={size}
            disabled={generating}
            onChange={(event) =>
              setSize(event.target.value as ImageJob["size"])
            }
          >
            <option value="1K">1K · quick concept</option>
            <option value="2K">2K · standard</option>
            <option value="4K">4K · detailed</option>
          </select>
        </label>
        <label className="creative-prompt">
          <span>What should it become?</span>
          <textarea
            autoFocus
            rows={3}
            maxLength={4000}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A top-view sofa with clean black lines…"
          />
        </label>
        <div className="creative-chips" aria-label="Sketch prompt starters">
          <button
            onClick={() =>
              setPrompt(
                "A top-view furniture symbol for an architectural floor plan. Keep the sketched silhouette and proportions. Fine black lines, plain white background.",
              )
            }
          >
            Furniture
          </button>
          <button
            onClick={() =>
              setPrompt(
                "Clean up this floor-plan sketch. Preserve the room layout, wall positions and openings. Fine black architectural linework on white. Do not add dimensions or rooms.",
              )
            }
          >
            Floor plan
          </button>
          <button onClick={() => setPrompt(SKETCH_PROMPT)}>
            Clean linework
          </button>
        </div>
        {(stale || error) && (
          <div className="creative-warning">
            <small role="status">
              {error ||
                "Your sketch changed. Refresh the preview to include your edits."}
            </small>
            <button
              disabled={preparing || generating}
              onClick={() => void capture()}
            >
              <RefreshCw size={15} /> Refresh preview
            </button>
          </div>
        )}
        <footer className="creative-submit">
          {!generation.status.configured ? (
            <small>
              Nano Banana Pro is not connected. Configure the server’s Gemini
              key to generate.
            </small>
          ) : (
            <ImageGenerationActions
              sketch
              canGenerate={generation.status.canGenerate}
              paused={generation.status.paused}
              generating={generating}
              disabled={
                !snapshot || stale || preparing || !prompt.trim() || !!error
              }
              hasResult={job?.state === "ready" && !placed}
              submit={() => {
                if (snapshot)
                  void generation.submitSketch(snapshot, prompt, size);
              }}
            />
          )}
          {generation.error && <small role="status">{generation.error}</small>}
          {job && (
            <ImageGenerationResult
              key={job.id}
              job={job}
              add={generation.add}
              placed={placed}
              sourcePreview={
                job.sourceKey === snapshot?.hash ? snapshot?.url : undefined
              }
            />
          )}
          {placed && (
            <button
              type="button"
              className="creative-secondary"
              onClick={() => {
                const existing = ids.filter((id) => !!editor.getShape(id));
                if (!existing.length) return;
                editor.select(...existing);
                editor.zoomToSelection({ animation: { duration: 200 } });
                close();
              }}
            >
              Select original sketch
            </button>
          )}
        </footer>
      </div>
    </section>
  );
}
